import React, { useState } from 'react';
import Spinner from 'components/Spinner';
import { Row, Col, Button, Table } from 'react-bootstrap';
import FormInput from 'components/forms/FormInput';
import IconRadioGroup from 'components/forms/IconRadioGroup';
import { useIntl } from 'react-intl';

const DISCOUNT_TYPE_VALUE = 'value';
const DISCOUNT_TYPE_PERCENT = 'percent';

const log = (...args) => console.log('ClientProductGroupDiscounts', ...args);

function ConfirmDelete({ callback }) {
  const { messages } = useIntl();
  const [isShowConfirm, setIsShowConfirm] = useState(false);

  if (isShowConfirm) {
    return (
      <div class="pull-right">
        {messages.are_you_sure}{' '}
        <Button className="text-danger btn-rounded" bsSize="small" label="Yes" onClick={callback}>
          <i className="fa fa-times" /> {messages.yes}
        </Button>
        <Button className="text-success btn-rounded" bsSize="small" label="No" onClick={() => setIsShowConfirm(false)}>
          {messages.no}
        </Button>
      </div>
    );
  }

  return (
    <>
      <Button
        className="text-danger pull-right"
        bsSize="small"
        label={messages.delete}
        onClick={() => setIsShowConfirm(true)}
      >
        <i className="fa fa-times" />
      </Button>
    </>
  );
}

function ClientProductGroupDiscounts({ entityLoaded, entity, changeEntity, disabled }) {
  if (!entityLoaded) {
    return (
      <div style={{ margin: 'auto' }}>
        <Spinner />
      </div>
    );
  }

  // Local state
  const [number, setNumber] = useState();
  const [value, setValue] = useState();
  const [discountType, setDiscountType] = useState(DISCOUNT_TYPE_VALUE);
  const [maxDiscount, setMaxDiscount] = useState();

  const { messages } = useIntl();

  const discounts = entity?.getDiscounts?.() || [];
  const isDiscountsEmpty = discounts.length === 0;
  const isShowSecondForm = discounts.length > 10;

  // const edit = (id, number) => {
  //   // TODO: replace row with an edit form?
  //   // maybe only allow to delete a row and create a new instead?
  // };

  const remove = (xId, xNumber) => {
    const updatedDiscounts = discounts.filter(({ id, number }) => (id ? id !== xId : number !== xNumber));
    const newEntity = { ...entity.updateValue('discounts', updatedDiscounts) };
    changeEntity(newEntity);
  };

  const rows = discounts.map(({ id, number, type, valueDiscount, percentDiscount, maxOrderDiscount }) => (
    <tr key={id ?? number}>
      <td>{number}</td>
      <td>{type === DISCOUNT_TYPE_VALUE ? '€' + valueDiscount : percentDiscount + '%'}</td>
      <td>€{maxOrderDiscount}</td>
      <td>
        {/* <Button label="Edit" onClick={() => edit(id, number)} size={12}>
          <i className="fa fa-pencil-alt" />
        </Button> */}
        <ConfirmDelete callback={() => remove(id, number)} />
      </td>
    </tr>
  ));

  const discountsTable = (
    <Table hover>
      <thead>
        <tr>
          <th>Number</th>
          <th>Type and value</th>
          <th>Max discount</th>
          <th className="pull-right">Actions</th>
        </tr>
      </thead>
      <tbody>{rows}</tbody>
    </Table>
  );

  // local validation
  const isNumberUnique = () => !discounts.some(({ number: n }) => n === number);
  const isPercentValid = () =>
    discountType === DISCOUNT_TYPE_VALUE ||
    value === undefined ||
    (discountType === DISCOUNT_TYPE_PERCENT && Number(value) <= 100 && Number(value) > 0);
  const isValueValid = () =>
    discountType === DISCOUNT_TYPE_PERCENT ||
    value === undefined ||
    (discountType === DISCOUNT_TYPE_VALUE && Number(value) > 0);
  const isFormValid = () => {
    if (!number?.length) return false;
    if (!isNumberUnique) return false;
    if (!value) return false;
    if (!discountType) return false;
    if (!maxDiscount) return false;

    return true;
  };

  const saveNewRow = () => {
    const row = {
      number,
      type: discountType,
      valueDiscount: discountType === DISCOUNT_TYPE_VALUE ? value : 0,
      percentDiscount: discountType === DISCOUNT_TYPE_PERCENT ? value : 0,
      maxOrderDiscount: maxDiscount,
    };

    setNumber('');
    const newEntity = { ...entity.updateValue('discounts', [row, ...discounts]) };
    changeEntity(newEntity);
  };

  const getInputDiscountValue = () => (
    <FormInput
      label="discount value"
      name="valueDiscount"
      value={value}
      validation={{ valid: isValueValid(), helpText: 'Must greater than 0' }}
      onChange={(_, value) => setValue(value)}
      disabled={disabled}
      type="number"
      prefixAddon="€"
    />
  );

  const getInputDiscountPercent = () => (
    <FormInput
      label="discount percent"
      name="percentDiscount"
      value={value}
      validation={{ valid: isPercentValid(), helpText: messages.discounts.must_be_0_to_100 }}
      onChange={(_, value) => setValue(value)}
      disabled={disabled}
      type="number"
      prefixAddon="%"
    />
  );

  const getForm = () => (
    <>
      <Row>
        <Col xs={12} md={3}>
          <FormInput
            label="product number"
            name="number"
            value={number}
            validation={{ valid: isNumberUnique(), helpText: messages.discounts.number_must_be_unique }}
            onChange={(_, value) => setNumber(value)}
            disabled={disabled}
            type="string"
            prefixAddon="#"
          />
        </Col>
        <Col xs={6} md={1}>
          <IconRadioGroup
            name="discount_type"
            variant="primary"
            xinline
            data={[
              { value: DISCOUNT_TYPE_VALUE, icon: 'fa-minus', label: '€' },
              { value: DISCOUNT_TYPE_PERCENT, icon: 'fa-percent', label: '%' },
            ]}
            value={discountType}
            onChange={setDiscountType}
          />
        </Col>
        <Col xs={6} md={2}>
          {discountType === DISCOUNT_TYPE_VALUE ? getInputDiscountValue() : getInputDiscountPercent()}
        </Col>
        <Col xs={6} md={3}>
          <FormInput
            label="max discount"
            name="number"
            value={maxDiscount}
            onChange={(_, value) => setMaxDiscount(value)}
            disabled={disabled}
            type="number"
            prefixAddon="€"
          />
        </Col>
        <Col xs={12} md={3}>
          <Button
            style={{ marginTop: '25px' }}
            bsStyle={isFormValid() ? 'primary' : 'default'}
            disabled={!isFormValid()}
            onClick={saveNewRow}
          >
            <i className="fa fa-plus m-r-10" /> {messages.add}
          </Button>
        </Col>
      </Row>
    </>
  );

  const noDiscountsHint = (
    <>
      <h4 style={{ margin: '1em', textAlign: 'center' }}>{messages.discounts.none_yet_add_some}</h4>
    </>
  );

  return (
    <>
      <Row>
        <Col xs="12">
          <p>{messages.discounts.intro_text}</p>
        </Col>
      </Row>
      {getForm()}
      {isDiscountsEmpty ? (
        noDiscountsHint
      ) : (
        <>
          <Row>
            <Col xs="12">
              <h3>{messages.discount}</h3>
            </Col>
          </Row>

          {discountsTable}
        </>
      )}
      {isShowSecondForm && getForm()}
    </>
  );
}

export default ClientProductGroupDiscounts;
